import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cardFrame from '../assets/cardFrame.png';
import sampleCardArt from '../assets/sampleCardArt.jpg';

/**
 * ThirdCardDisplay renders the generated Third Card with a beautiful
 * framed display, including artwork, Roman numerals, title, and description.
 */
export default function ThirdCardDisplay({ card, card1, card2 }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  if (!card) return null;
  
  console.log('🖼️ ThirdCardDisplay rendering with imageURL:', card.imageURL);
  console.log('🖼️ Image generation error:', card.imageGenerationError);
  
  // Only use DALL-E image if available, no fallback to sample
  const hasGeneratedImage = !!card.imageURL;
  const hasContentFilterError = card.imageGenerationError === 'CONTENT_FILTER';
  const cardArtwork = card.imageURL; // Don't fallback to sampleCardArt
  
  console.log('🖼️ Card artwork URL:', cardArtwork);
  console.log('🖼️ Has generated image:', hasGeneratedImage);
  console.log('🖼️ Image loaded state:', imageLoaded);
  
  const handleImageLoad = () => {
    console.log('🖼️ ✅ Image loaded successfully');
    setImageLoaded(true);
  };
  
  const handleImageError = (e) => {
    console.error('🖼️ ❌ Image failed to load:', e);
    console.error('🖼️ Failed URL:', cardArtwork);
    setImageError(true);
    setImageLoaded(true); // Show card anyway with fallback
  };
  
  // Helper to format orientation label
  const formatOrientation = (orientation) => {
    if (!orientation || orientation === 'upright') return '';
    return 'Reversed';
  };
  
  return (
    <div className="card-altar text-center mt-8">
      {/* First Time Generation Celebration */}
      {card.isNewlyGenerated && (
        <div className="first-time-celebration mb-6 p-4 rounded-lg bg-gradient-to-r from-purple-900/30 via-amber-900/30 to-purple-900/30 border-2 border-amber-500/50">
          <div className="text-2xl mb-2">✨ 🎉 ✨</div>
          <p className="text-amber-300 font-semibold text-lg mb-2">
            Congratulations, Pioneer of the Mystic Path!
          </p>
          <p className="text-gray-300 text-sm">
            You are the <span className="text-amber-400 font-bold">first seeker</span> to discover this sacred combination.
            <br />
            This unique Third Card has been woven into existence through your journey.
          </p>
        </div>
      )}
      
      {/* Card Synthesis Display */}
      {card1 && card2 && (
        <div className="card-synthesis">
          <div className="synthesis-card">
            <span className="synthesis-card-name">{card1.name}</span>
            {formatOrientation(card1.orientation) && (
              <span className="synthesis-card-orientation">({formatOrientation(card1.orientation)})</span>
            )}
          </div>
          
          <span className="synthesis-spiral">∞</span>
          
          <div className="synthesis-card">
            <span className="synthesis-card-name">{card2.name}</span>
            {formatOrientation(card2.orientation) && (
              <span className="synthesis-card-orientation">({formatOrientation(card2.orientation)})</span>
            )}
          </div>
          
          <span className="synthesis-spiral">⟹</span>
          
          <div className="synthesis-result">
            <span>{card.thirdCardTitle}</span>
          </div>
        </div>
      )}
      
      <h3 className="heading-tertiary mb-6">{card.thirdCardTitle}</h3>
      
      <div className="divider-sacred"></div>
      
      {/* Card Description - Show first since it loads faster */}
      <div className="body-text text-left max-w-2xl mx-auto leading-relaxed mt-8 mb-8">
        {card.description.split('\n').map((paragraph, index) => (
          <p key={index} className="mb-4">
            {paragraph}
          </p>
        ))}
      </div>
      
      {/* Framed Card Display - Only show if we have a generated image */}
      {hasGeneratedImage && (
        <div className="flex justify-center mb-8">
          {!imageLoaded && (
            <div className="third-card-loading">
              <div className="loading-spinner"></div>
              <p className="loading-text">Materializing Sacred Artwork...</p>
            </div>
          )}
          
          <div className={`third-card-container ${!imageLoaded ? 'hidden' : ''}`}>
            {/* Card artwork behind the frame */}
            <img 
              src={cardArtwork}
              alt="Card Artwork"
              className="third-card-artwork"
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            
            {/* Ornate frame overlay with built-in Roman numeral III */}
            <img 
              src={cardFrame}
              alt="Card Frame"
              className="third-card-frame"
            />
            
            {/* Card Title at Bottom */}
            <div className="third-card-title">
              {card.thirdCardTitle}
            </div>
          </div>
        </div>
      )}
      
      {/* Show message if no image was generated */}
      {!hasGeneratedImage && (
        <div className="text-center my-8">
          {hasContentFilterError ? (
            <>
              <p className="status-mystical text-amber-400">
                ⚠ Content Filter Alert ⚠
              </p>
              <p className="body-text text-sm mt-4 text-gray-300 max-w-xl mx-auto leading-relaxed">
                The image generator's safety filters mistakenly flagged this tarot card combination. 
                This is a known limitation with AI image generation for spiritual/oracle content.
              </p>
              <p className="body-text text-sm mt-3 text-gray-400">
                Try regenerating with different cards, or enjoy the wisdom in the text above. 
                The card's guidance remains powerful without imagery.
              </p>
            </>
          ) : (
            <>
              <p className="status-mystical">
                ✦ The Oracle's Vision Awaits ✦
              </p>
              <p className="body-text text-sm mt-2 text-gray-400">
                Sacred artwork will manifest when this card is regenerated
              </p>
            </>
          )}
        </div>
      )}
      
      <div className="status-mystical mt-6">
        ✦ The Third Path is Revealed ✦
      </div>
    </div>
  );
}

ThirdCardDisplay.propTypes = {
  card: PropTypes.shape({
    thirdCardTitle: PropTypes.string.isRequired,
    thirdCardDescription: PropTypes.string.isRequired,
    imageURL: PropTypes.string,
    romanNumeral: PropTypes.string,
    contentFilterError: PropTypes.bool,
  }).isRequired,
  card1: PropTypes.shape({
    name: PropTypes.string.isRequired,
    orientation: PropTypes.string,
  }).isRequired,
  card2: PropTypes.shape({
    name: PropTypes.string.isRequired,
    orientation: PropTypes.string,
  }).isRequired,
};